import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { paymentGatewayApi } from "../API/paymentGatewayApi";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import Menu from "../Component/Menu";
import TextfieldComponent from "../Component/TextfieldComponent";
import UploadFilesService from "../Common/upload/UploadFilesService";
import FileServices from "../Common/upload/FileServices";
import GatewayLogo from "./GatewayLogo";
import Breadcrumb from "../NewUI/NewComponents/Breadcrumb";
import Swal from "sweetalert2";

const textFields = [
  { name: "code", label: "Code", required: true, span: 4 },
  { name: "displayName", label: "Display Name", required: true, span: 4 },
  { name: "provider", label: "Provider", span: 4 },
  { name: "apiUrl", label: "API URL", span: 4 },
  { name: "webhookUrl", label: "Webhook URL", span: 4 },
  { name: "priority", label: "Priority", span: 4, numeric: true },
  { name: "documentationUrl", label: "Documentation URL", wide: true },
  { name: "description", label: "Description", span: 6 },
];

const boolFields = [
  { name: "active", label: "Active" },
  { name: "sandbox", label: "Sandbox" },
  { name: "supportsSubscription", label: "Supports Subscription" },
  { name: "supportsRefund", label: "Supports Refund" },
  { name: "supportsPartialRefund", label: "Supports Partial Refund" },
  { name: "supportsWebhook", label: "Supports Webhook" },
  { name: "supportsTokenization", label: "Supports Tokenization" },
];

const LOGO_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"];

export default function GatewayForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(id);
  const returnTo = location.state?.returnTo;
  const draft = location.state?.draft;
  const [form, setForm] = useState({
    code: "",
    displayName: "",
    provider: "",
    apiUrl: "",
    webhookUrl: "",
    documentationUrl: "",
    logoUrl: "",
    description: "",
    priority: 0,
    active: true,
    sandbox: true,
    supportsSubscription: true,
    supportsRefund: true,
    supportsPartialRefund: false,
    supportsWebhook: false,
    supportsTokenization: false,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoMsg, setLogoMsg] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (isEdit) {
      paymentGatewayApi.getById(id).then((res) => setForm((p) => ({ ...p, ...res.data }))).catch(() => Swal.fire("Failed to load gateway", "", "error"));
    }
  }, [id]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const pickLogo = (file) => {
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      setLogoMsg("Only PNG, JPG, JPEG and SVG files are allowed");
      return;
    }
    if (file.size / (1024 * 1024) > 2) {
      setLogoMsg("Logo size must be less than 2MB");
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setLogoMsg("");
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    setLogoBusy(true);
    setLogoMsg("");
    try {
      const year = new Date().getFullYear();
      const day = new Date().getDate();
      const curmonth = new Date().toLocaleString("default", { month: "long" });
      await UploadFilesService.logoupload(logoFile, year, curmonth, "gateway-logo", "gateways", day);
      const response = await FileServices.getFiles1(logoFile.name);
      const names = response.data.map((e) => e.name);
      if (!names.length) throw new Error("Upload verification failed");
      const path = `gateways/gateway-logo/gateways/${year}/${curmonth}/${day}`;
      setForm((p) => ({ ...p, logoUrl: `${path}|${names[0]}` }));
      setLogoFile(null);
      setLogoPreview(null);
      setLogoMsg("Logo uploaded - it will be saved with the gateway.");
    } catch (e) {
      setLogoMsg("Logo upload failed - you can still paste a Logo URL instead.");
    } finally {
      setLogoBusy(false);
    }
  };

  const submit = async () => {
    if (!form.code?.trim() || !form.displayName?.trim()) {
      Swal.fire("Code and Display Name are required", "", "warning");
      return;
    }
    try {
      let newId = id;
      if (isEdit) await paymentGatewayApi.update(id, form);
      else {
        const res = await paymentGatewayApi.create(form);
        newId = res.data?.id || res.data?.data?.id || null;
      }
      Swal.fire("Saved", "", "success").then(() => {
        if (returnTo && !isEdit && newId) navigate(returnTo, { state: { newGatewayId: newId, draft } });
        else if (returnTo) navigate(returnTo, { state: { draft } });
        else navigate("/gateways");
      });
    } catch (e) {
      Swal.fire(e?.response?.data?.message || "Save failed", "", "error");
    }
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className="UI-container">
          <div className="UI-header">
            <Breadcrumb breadcrumbs={[{ name: "Home", path: "/superadminmasters" }, { name: "Payment Gateways", path: "/gateways" }, { name: isEdit ? "Edit" : "Create", path: null }]} />
          </div>
          <div className="UI-form-card">
            <div className="form-row">
              {textFields.map((f) => (
                <fieldset className={f.span ? `col-md-${f.span}` : f.wide ? "col-md-6" : "col-md-3"} key={f.name}>
                  <TextfieldComponent type={f.numeric ? "number" : "text"} name={f.name} label={f.label} value={form[f.name] ?? ""} onChange={(e) => set(f.name, f.numeric ? Number(e.target.value) : e.target.value)} required={f.required} />
                </fieldset>
              ))}
              <fieldset className="col-md-6">
                <TextfieldComponent type="text" name="logoUrl" label="Logo URL (https://...)" value={/^https?:\/\//i.test(form.logoUrl || "") ? form.logoUrl : ""} onChange={(e) => set("logoUrl", e.target.value)} />
              </fieldset>
              <fieldset className="col-md-6">
                <div className="UI-form-group tw-pb-1">
                  <label className="UI-label">Logo Upload</label>
                  <div className="tw-flex tw-items-center tw-gap-3 tw-pt-1">
                    <input ref={logoInputRef} type="file" accept=".png,.jpg,.jpeg,.svg" className="tw-hidden" onChange={(e) => pickLogo(e.target.files?.[0])} />
                    <button type="button" className="UI-btn UI-btn-outline" onClick={() => logoInputRef.current?.click()}>Choose File</button>
                    {logoFile && <button type="button" className="UI-btn UI-btn-primary" disabled={logoBusy} onClick={uploadLogo}>{logoBusy ? "Uploading..." : "Upload"}</button>}
                    <GatewayLogo logoUrl={logoPreview || form.logoUrl} size={40} />
                  </div>
                  {logoFile && <div className="tw-text-xs tw-text-slate-500 tw-pt-1">{logoFile.name} ({(logoFile.size / 1024).toFixed(1)} KB)</div>}
                  {logoMsg && <div className="tw-text-xs tw-pt-1 tw-text-slate-500">{logoMsg}</div>}
                  <div className="tw-text-[11px] tw-text-slate-400 tw-pt-1">Upload PNG/JPG/SVG up to 2MB, or paste a URL above.</div>
                </div>
              </fieldset>
              {boolFields.map((f) => (
                <fieldset className="col-md-3" key={f.name}>
                  <div className="UI-form-group tw-pb-1">
                    <label className="UI-label">{f.label}</label>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-pt-2">
                      <input type="checkbox" className="tw-w-5 tw-h-5 tw-accent-[#fdd60e] tw-cursor-pointer" checked={Boolean(form[f.name])} onChange={(e) => set(f.name, e.target.checked)} />
                      <span className="tw-text-sm">{form[f.name] ? "Yes" : "No"}</span>
                    </div>
                  </div>
                </fieldset>
              ))}
              <fieldset className="col-md-12 tw-flex tw-justify-end">
                <button className="UI-btn UI-btn-primary tw-mr-2" type="button" onClick={submit}>Save</button>
                <Link as="button" className="UI-btn UI-btn-outline col-md-auto col-sm-12" to={{ pathname: "/gateways" }}>Cancel</Link>
              </fieldset>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
